import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, X } from 'lucide-react'
import { useScrollLock } from '@/hooks/useScrollLock'

// ─── Constantes ──────────────────────────────────────────────────────────────

/** Taille visuelle du cadre carré (px) */
const CROP_SIZE = 280
/** Taille de sortie du canvas (résolution finale de l'avatar) */
const OUTPUT_SIZE = 400
/** Qualité JPEG */
const JPEG_QUALITY = 0.85
/** Zoom minimum (couvre le cadre) / maximum */
const MIN_SCALE = 1
const MAX_SCALE = 3
/** Taille minimale source acceptée */
const MIN_SOURCE_SIDE = 100
/** Ratio considéré comme « déjà carré » — évite la modale si pas nécessaire */
const SQUARE_MIN = 0.95
const SQUARE_MAX = 1.05
/** Flag localStorage pour ne montrer le hint pinch qu'une fois */
const HINT_KEY = 'selah_avatar_pinch_hint_seen'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CropAnalysis {
  width: number
  height: number
  tooSmall: boolean
  isSquare: boolean
}

interface Props {
  file: File
  onCancel: () => void
  /** Blob JPEG 400×400 prêt à uploader */
  onConfirm: (blob: Blob) => Promise<void> | void
}

// ─── Helpers exportés ────────────────────────────────────────────────────────

/** Analyse la taille et le ratio d'une image pour décider du flow. */
export function analyzeImage(file: File | Blob): Promise<CropAnalysis> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const ratio = img.width / Math.max(1, img.height)
      resolve({
        width: img.width,
        height: img.height,
        tooSmall: img.width < MIN_SOURCE_SIDE || img.height < MIN_SOURCE_SIDE,
        isSquare: ratio >= SQUARE_MIN && ratio <= SQUARE_MAX,
      })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Image invalide'))
    }
    img.src = url
  })
}

/** Crop carré centré — pour le fallback auto (image déjà carrée). */
export async function autoCenterCrop(file: File | Blob): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = () => reject(new Error('Load failed'))
      i.src = url
    })
    const side = Math.min(img.width, img.height)
    const sx = (img.width - side) / 2
    const sy = (img.height - side) / 2
    const canvas = document.createElement('canvas')
    canvas.width = OUTPUT_SIZE
    canvas.height = OUTPUT_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas context unavailable')
    ctx.drawImage(img, sx, sy, side, side, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE)
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        'image/jpeg',
        JPEG_QUALITY,
      )
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

// ─── Composant ───────────────────────────────────────────────────────────────

export function AvatarCropper({ file, onCancel, onConfirm }: Props) {
  useScrollLock()

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null)
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [submitting, setSubmitting] = useState(false)
  const [showHint, setShowHint] = useState(false)

  // Refs pour les gestures (pas d'état : ne déclenche pas de re-render)
  const panStart = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null)
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null)

  // ── Préparation : chargement image + dimensions ──────────────────────────
  useEffect(() => {
    const url = URL.createObjectURL(file)
    setImageUrl(url)
    const img = new Image()
    img.onload = () => {
      setImgSize({ w: img.width, h: img.height })
      // Hint pinch : affiché une fois par utilisateur
      if (!localStorage.getItem(HINT_KEY)) {
        setShowHint(true)
        localStorage.setItem(HINT_KEY, '1')
        setTimeout(() => setShowHint(false), 3000)
      }
    }
    img.src = url
    return () => { URL.revokeObjectURL(url) }
  }, [file])

  // ── Calculs dérivés : base image fit "cover" dans le cadre ───────────────
  // k = display pixels par image pixel (au scale 1, l'image couvre juste le cadre)
  const baseK = imgSize
    ? CROP_SIZE / Math.min(imgSize.w, imgSize.h)
    : 1
  const k = baseK * scale
  const displayW = imgSize ? imgSize.w * k : 0
  const displayH = imgSize ? imgSize.h * k : 0

  // Centre initial : image centrée dans le cadre
  useEffect(() => {
    if (!imgSize) return
    setOffset({
      x: (CROP_SIZE - imgSize.w * baseK) / 2,
      y: (CROP_SIZE - imgSize.h * baseK) / 2,
    })
    setScale(1)
  }, [imgSize, baseK])

  // Clamp offset pour que l'image couvre toujours le cadre
  const clampOffset = (x: number, y: number, dispW: number, dispH: number) => ({
    x: Math.min(0, Math.max(CROP_SIZE - dispW, x)),
    y: Math.min(0, Math.max(CROP_SIZE - dispH, y)),
  })

  // ── Touch handlers ────────────────────────────────────────────────────────
  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    if (e.touches.length === 1) {
      const t = e.touches[0]
      panStart.current = { x: t.clientX, y: t.clientY, offsetX: offset.x, offsetY: offset.y }
      pinchStart.current = null
    } else if (e.touches.length >= 2) {
      const [a, b] = [e.touches[0], e.touches[1]]
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
      pinchStart.current = { dist, scale }
      panStart.current = null
    }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    if (e.touches.length === 1 && panStart.current) {
      const t = e.touches[0]
      const dx = t.clientX - panStart.current.x
      const dy = t.clientY - panStart.current.y
      const next = clampOffset(panStart.current.offsetX + dx, panStart.current.offsetY + dy, displayW, displayH)
      setOffset(next)
    } else if (e.touches.length >= 2 && pinchStart.current && imgSize) {
      const [a, b] = [e.touches[0], e.touches[1]]
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
      const factor = dist / pinchStart.current.dist
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchStart.current.scale * factor))
      // Zoom centré sur le milieu du cadre
      const centerX = CROP_SIZE / 2
      const centerY = CROP_SIZE / 2
      const ratio = newScale / scale
      const newDispW = imgSize.w * baseK * newScale
      const newDispH = imgSize.h * baseK * newScale
      const nextX = centerX - (centerX - offset.x) * ratio
      const nextY = centerY - (centerY - offset.y) * ratio
      setScale(newScale)
      setOffset(clampOffset(nextX, nextY, newDispW, newDispH))
    }
  }

  const onTouchEnd = () => {
    panStart.current = null
    pinchStart.current = null
  }

  // ── Mouse wheel zoom (desktop) ────────────────────────────────────────────
  const onWheel = (e: React.WheelEvent) => {
    if (!imgSize) return
    e.preventDefault()
    const delta = -e.deltaY * 0.002
    const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + delta))
    const ratio = newScale / scale
    const centerX = CROP_SIZE / 2
    const centerY = CROP_SIZE / 2
    const newDispW = imgSize.w * baseK * newScale
    const newDispH = imgSize.h * baseK * newScale
    const nextX = centerX - (centerX - offset.x) * ratio
    const nextY = centerY - (centerY - offset.y) * ratio
    setScale(newScale)
    setOffset(clampOffset(nextX, nextY, newDispW, newDispH))
  }

  // ── Mouse drag (desktop) ──────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    panStart.current = { x: e.clientX, y: e.clientY, offsetX: offset.x, offsetY: offset.y }
  }
  const onMouseMove = (e: React.MouseEvent) => {
    if (!panStart.current) return
    const dx = e.clientX - panStart.current.x
    const dy = e.clientY - panStart.current.y
    setOffset(clampOffset(panStart.current.offsetX + dx, panStart.current.offsetY + dy, displayW, displayH))
  }
  const onMouseUp = () => { panStart.current = null }

  // ── Crop & export ─────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!imgSize || !imageUrl || submitting) return
    setSubmitting(true)
    try {
      // Source rect en pixels image
      const sx = -offset.x / k
      const sy = -offset.y / k
      const sw = CROP_SIZE / k
      const sh = CROP_SIZE / k

      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image()
        i.onload = () => resolve(i)
        i.onerror = () => reject(new Error('Load failed'))
        i.src = imageUrl
      })

      const canvas = document.createElement('canvas')
      canvas.width = OUTPUT_SIZE
      canvas.height = OUTPUT_SIZE
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas context unavailable')
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE)

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          b => (b ? resolve(b) : reject(new Error('toBlob failed'))),
          'image/jpeg',
          JPEG_QUALITY,
        )
      })
      await onConfirm(blob)
    } catch {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        key="cropper-backdrop"
        data-modal="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          zIndex: 1100,
          backgroundColor: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <motion.div
          key="cropper-card"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          style={{
            width: '100%',
            maxWidth: 360,
            backgroundColor: 'var(--selah-card)',
            border: '1px solid var(--selah-border)',
            borderRadius: 20,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
          }}
        >
          {/* Header */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2
              style={{
                fontWeight: 600,
                fontSize: 18,
                color: 'var(--selah-text)',
                margin: 0,
              }}
            >
              Recadrer
            </h2>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Fermer"
              style={{
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 8,
                backgroundColor: 'transparent',
                border: 'none',
                color: 'var(--selah-text-secondary)',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Zone de recadrage */}
          <div
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            style={{
              position: 'relative',
              width: CROP_SIZE,
              height: CROP_SIZE,
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: '#000', // intentional: dark canvas behind photo crop
              touchAction: 'none',
              userSelect: 'none',
              cursor: 'grab',
            }}
          >
            {imageUrl && imgSize && (
              <img
                src={imageUrl}
                alt="À recadrer"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: offset.x,
                  top: offset.y,
                  width: displayW,
                  height: displayH,
                  maxWidth: 'none',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
            )}

            {/* Overlay sombre avec trou circulaire — box-shadow cheat pour masker */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: CROP_SIZE,
                  height: CROP_SIZE,
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50%',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
                  border: '2px solid #FFFFFF',
                }}
              />
            </div>

            {/* Hint "Zoom avec deux doigts" (3s) */}
            <AnimatePresence>
              {showHint && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '6px 10px',
                    borderRadius: 999,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#FFFFFF', // intentional: hint text on dark overlay
                    fontSize: 11,
                    fontWeight: 500,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Zoom avec deux doigts
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Slider zoom (desktop + fallback mobile) */}
          <input
            type="range"
            min={MIN_SCALE * 100}
            max={MAX_SCALE * 100}
            step={1}
            value={Math.round(scale * 100)}
            onChange={e => {
              if (!imgSize) return
              const newScale = Number(e.target.value) / 100
              const ratio = newScale / scale
              const centerX = CROP_SIZE / 2
              const centerY = CROP_SIZE / 2
              const newDispW = imgSize.w * baseK * newScale
              const newDispH = imgSize.h * baseK * newScale
              const nextX = centerX - (centerX - offset.x) * ratio
              const nextY = centerY - (centerY - offset.y) * ratio
              setScale(newScale)
              setOffset(clampOffset(nextX, nextY, newDispW, newDispH))
            }}
            aria-label="Zoom"
            style={{
              width: '100%',
              accentColor: 'var(--selah-accent)',
            }}
          />

          {/* Actions */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting || !imgSize}
              style={{
                width: '100%',
                padding: '12px 24px',
                borderRadius: 24,
                border: 'none',
                backgroundColor: 'var(--selah-button-bg)',
                color: 'var(--selah-button-text)',
                fontSize: 16,
                fontWeight: 600,
                cursor: submitting ? 'wait' : 'pointer',
                opacity: submitting || !imgSize ? 0.5 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? 'Envoi…' : 'Valider'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '10px',
                background: 'none',
                border: 'none',
                color: 'var(--selah-text-secondary)',
                fontSize: 14,
                fontWeight: 500,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              Annuler
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
